/**
 * Die Gliederung des Gewerks Lüftung (KG 430) — an EINER Stelle.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * HERKUNFT (TV5, 18.10.2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis TV5 standen die zehn Unterseiten der Lüftung als Einzeleinträge in
 * `navigation/struktur.ts`. Nach TX-STANDARD S1.1 hat jedes Gewerk genau EINE
 * Gliederung in `seiten/<gewerk>/abschnitte.ts`; die Bahn, die das Gewerk zuerst
 * anfasst, legt sie an (für die Lüftung: Bahn TV). `struktur.ts` liest sie über
 * `lueftungEintraege()` ein, nach dem Muster von `seiten/heizung/abschnitte.ts`.
 *
 * PFAD- UND SCHLÜSSELGLEICH: Die zehn bisherigen Einträge behalten Pfad,
 * Schlüssel, Beschriftung, Sinnbild und Zwischenzeile (`art`) — ein
 * ausgelieferter Pfad wird nie geändert (S1.1), und am Schlüssel hängen
 * Favoriten und Tests. Deshalb steht hier, anders als bei Heizung, der Schlüssel
 * ausdrücklich, und die Zwischenzeile heißt weiter nach der Art
 * (`planung`, `berechnung`, `daten`, `pruefung`). Ein Vitest
 * (`abschnitte.test.ts`) vergleicht die Einträge mit dem Stand vor dem Umzug.
 *
 * Neu sind die vier Prüfungsseiten aus TV5. Sie tragen einen `hinweis`; die
 * bisherigen zehn hatten keinen und bekommen auch jetzt keinen (gleich bleibt
 * gleich).
 */

/** Das Sinnbild eines Eintrags; `struktur.ts` übersetzt es in die Komponente aus `Icons.tsx`. */
export type LueftungSinnbild =
  | 'lueftung' | 'berechnung' | 'tabelle' | 'datei' | 'hochladen' | 'standard' | 'warnung';

export interface LueftungEintrag {
  /** Stabiler Schlüssel (Favoriten, Tests) — für die bisherigen zehn unverändert. */
  schluessel: string;
  /** Der Pfad UNTER /projekte/:id/ — genau wie in App.tsx. */
  pfad: string;
  name: string;
  sinnbild: LueftungSinnbild;
  /** Wozu die Seite da ist. Erscheint als Erläuterung in Baum und Befehlspalette. */
  hinweis?: string;
}

export interface LueftungAbschnitt {
  /** Die Zwischenzeile im Baum: eine der vier Arten aus `ART_BESCHRIFTUNG`. */
  art: 'planung' | 'berechnung' | 'daten' | 'pruefung';
  eintraege: LueftungEintrag[];
}

export const LUEFTUNG_ABSCHNITTE: LueftungAbschnitt[] = [
  {
    art: 'planung',
    eintraege: [
      { schluessel: 'lueftung-anlagen', pfad: 'lueftung/anlagen', name: 'Anlagenliste', sinnbild: 'lueftung' },
      { schluessel: 'lueftung-kvs', pfad: 'lueftung/kvs', name: 'Kreislaufverbund (KVS)', sinnbild: 'lueftung' },
    ],
  },
  {
    art: 'berechnung',
    eintraege: [
      { schluessel: 'lueftung-druckverlust', pfad: 'lueftung/druckverlust', name: 'Druckverlust', sinnbild: 'berechnung' },
      { schluessel: 'lueftung-schall', pfad: 'lueftung/schall', name: 'Schall', sinnbild: 'berechnung' },
      { schluessel: 'lueftung-raumschall', pfad: 'lueftung/raumschall', name: 'Raumschall', sinnbild: 'berechnung' },
    ],
  },
  {
    art: 'daten',
    eintraege: [
      // `anlagen` (Anlagenseiten mit AKS, Gerät und Datenblattstand) hatte bis
      // zum 20.09.2026 keinen Menüweg; seitdem steht sie hier.
      { schluessel: 'lueftung-anlagenseiten', pfad: 'anlagen', name: 'Anlagenseiten', sinnbild: 'tabelle' },
      { schluessel: 'lueftung-datenblaetter', pfad: 'lueftung/datenblaetter', name: 'Datenblätter', sinnbild: 'datei' },
      { schluessel: 'lueftung-importe', pfad: 'lueftung/importe', name: 'Datenblattimporte', sinnbild: 'hochladen' },
      { schluessel: 'lueftung-vorlagen', pfad: 'lueftung/vorlagen', name: 'Vorlagen', sinnbild: 'standard' },
      // TV8 (Eigentümerauftrag 20.10.2026): eigener Menüpunkt; mit TV5 aus navigation/struktur.ts hierher umgezogen
      // (R4-Anpassung RQ1, wie der TV7-Pick 2e94232c).
      {
        schluessel: 'lueftung-brandschutzklappen',
        pfad: 'lueftung/brandschutzklappen',
        name: 'Brandschutzklappen',
        sinnbild: 'tabelle',
        hinweis: 'Bestand der Brandschutzklappen: AKS-Schlüssel, Anlage, Ebene und Mengen - Baum und PDF LUE-BKL',
      },
    ],
  },
  {
    art: 'pruefung',
    eintraege: [
      { schluessel: 'lueftung-befunde', pfad: 'lueftung/befunde', name: 'Bauteilbefunde', sinnbild: 'warnung' },
      {
        schluessel: 'lueftung-geraetepruefung',
        pfad: 'lueftung/geraetepruefung',
        name: 'RLT-Geräteprüfung',
        sinnbild: 'warnung',
        hinweis: 'Je RLT-Gerät und Regel: ErP, SFP, WRG, GEG, ASR — erfüllt, nicht erfüllt, nicht prüfbar, entfällt',
      },
      {
        schluessel: 'lueftung-brandschutz',
        pfad: 'lueftung/brandschutz',
        name: 'Brandschutzklappen und Entrauchung',
        sinnbild: 'warnung',
        hinweis: 'BSK- und Entrauchungsliste: Übereinstimmung von Lüftungsmodell und Gebäudeautomation',
      },
      {
        schluessel: 'lueftung-luftmengen',
        pfad: 'lueftung/luftmengen',
        name: 'Luftmengen',
        sinnbild: 'warnung',
        hinweis: 'Soll und Ist der Luftmenge je Raum, alle Räume, mit Vorbehalten der Bewertung',
      },
      {
        schluessel: 'lueftung-pruefbefunde',
        pfad: 'lueftung/pruefbefunde',
        name: 'Befunde Kanalnetz und Raumpegel',
        sinnbild: 'warnung',
        hinweis: 'Alle Befunde des Druckverlustlaufs (Geschwindigkeit, Kennfeld, Regler, Raumpegel, Hygiene) mit Normbezug und Verweis',
      },
    ],
  },
];
